// ─────────────────────────────────────────────────────────────
// Settings design system (iter267)
//
// One shell, one header, one toolbar, one sub-tab strip for every
// Settings tab. Panels were each inventing their own header /
// spacing / callout treatment, so tabbing across Settings felt like
// five different apps. Everything below is presentational only.
// ─────────────────────────────────────────────────────────────
import { useState, useEffect, Fragment } from "react";
import { tr } from "@/i18n";
import { Search, AlertTriangle, Info, CheckCircle2, ShieldAlert, ChevronRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import SearchBar from "@/components/common/SearchBar";
import { PillRail, Pill, PillDivider } from "@/components/common/Segmented";

export const SettingsPanel = ({ children, testId, className = "" }) => (
  <div className={`data-table-container ${className}`} data-testid={testId}>
    {children}
  </div>
);

export const PanelCount = ({ value, testId, tone = "neutral" }) => {
  const tones = {
    neutral: "bg-slate-100 text-slate-600 border-slate-200",
    warn: "bg-amber-100 text-amber-800 border-amber-300",
    danger: "bg-rose-100 text-rose-800 border-rose-300",
    accent: "bg-violet-100 text-violet-800 border-violet-300",
  };
  return (
    <span
      className={`text-[10px] font-semibold tracking-wide border px-2 py-0.5 rounded-full whitespace-nowrap ${tones[tone] || tones.neutral}`}
      data-testid={testId}
    >
      {value}
    </span>
  );
};

// Single header for every panel: title · count · hint, actions right (no icons — UI sweep).
export const PanelHeader = ({ title, hint, count, countTone, actions, testId }) => (
  <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between gap-4 flex-wrap" data-testid={testId}>
    <div className="flex items-center gap-2.5 min-w-0">
      <h2 className="text-base font-semibold text-slate-900 truncate">{title}</h2>
      {count != null && count !== "" ? <PanelCount value={count} tone={countTone} /> : null}
      {hint ? <span className="text-xs text-slate-400 truncate hidden sm:inline">{hint}</span> : null}
    </div>
    {actions ? <div className="flex items-center gap-2 shrink-0">{actions}</div> : null}
  </div>
);

// Search left, extra filters inline, primary action right.
export const PanelToolbar = ({ searchValue, onSearchChange, searchPlaceholder = "Search…", searchTestId, children, actions }) => (
  <div className="px-5 py-3 border-b border-slate-200 bg-slate-50/60 flex items-center gap-3 flex-wrap">
    {onSearchChange ? (
      <div className="relative w-full sm:w-[280px]">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
        <Input
          value={searchValue}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder={searchPlaceholder}
          className="h-9 pl-9 text-sm"
          data-testid={searchTestId}
        />
      </div>
    ) : null}
    {children}
    {actions ? <div className="ml-auto flex items-center gap-2">{actions}</div> : null}
  </div>
);

export const PanelBody = ({ children, className = "" }) => (
  <div className={`px-5 py-4 ${className}`}>{children}</div>
);

// iter296 — every major section inside a tab collapses. The whole title
// row is the hit area (not just a chevron), the choice is remembered per
// section in localStorage, and anything passed as `actions` stops
// propagation so a Refresh button never folds the section under you.
export const CollapsibleSection = ({
  id, title, extra, actions,
  hint, defaultOpen = true, children, testId,
}) => {
  const storeKey = `abs.section.${id}`;
  const [open, setOpen] = useState(() => {
    const v = typeof window !== "undefined" ? window.localStorage.getItem(storeKey) : null;
    return v === null ? defaultOpen : v === "1";
  });
  const toggle = () => setOpen((o) => {
    try { window.localStorage.setItem(storeKey, o ? "0" : "1"); } catch { /* private mode */ }
    return !o;
  });

  return (
    <section className="border border-slate-200 rounded-lg overflow-hidden" data-testid={testId}>
      <div
        role="button"
        tabIndex={0}
        aria-expanded={open}
        onClick={toggle}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggle(); } }}
        className="flex items-center gap-2 px-3 py-2.5 cursor-pointer select-none hover:bg-slate-50 transition-colors"
        data-testid={`section-toggle-${id}`}
        title={open ? "Click to collapse" : "Click to expand"}
      >
        <ChevronRight className={`w-4 h-4 shrink-0 text-slate-400 transition-transform duration-150 ${open ? "rotate-90" : ""}`} />
        <h4 className="text-sm font-semibold text-slate-700 flex items-center gap-2 min-w-0">
          <span className="truncate">{title}</span>
          {extra}
        </h4>
        {hint ? <span className="text-xs text-slate-400 truncate hidden md:inline">{hint}</span> : null}
        {actions ? (
          <div className="ml-auto flex items-center gap-2 shrink-0" onClick={(e) => e.stopPropagation()} role="presentation">
            {actions}
          </div>
        ) : null}
      </div>
      {open && (
        <div className="px-3 pb-3 pt-2 border-t border-slate-100" data-testid={`section-body-${id}`}>
          {children}
        </div>
      )}
    </section>
  );
};

// Sub-tab pills — the one secondary nav style. `searchable` adds a search box that
// finds a pill by its label or any section header listed in `item.keywords`.
export function SubTabs({ items, value, onChange, testId, testIdPrefix, actions, searchable = false }) {
  const [q, setQ] = useState("");
  const needle = q.trim().toLowerCase();
  const shown = needle
    ? items.filter((t) => [t.label, ...(t.keywords || [])].some((s) => String(s).toLowerCase().includes(needle)))
    : items;
  useEffect(() => {
    if (needle && shown.length && !shown.some((t) => t.id === value)) onChange(shown[0].id);
  }, [needle]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="flex items-start gap-2 min-w-0">
      {searchable && (
        <SearchBar value={q} onChange={setQ} placeholder={tr("Search settings")} testId={`${testIdPrefix}-search`} width="w-56 shrink-0" />
      )}
      <PillRail testId={testId} className="min-w-0">
        {shown.map((t) => (
          <Fragment key={t.id}>
            {t.dividerBefore && <PillDivider />}
            <Pill on={value === t.id} onClick={() => onChange(t.id)} testId={`${testIdPrefix}-${t.id}`}>
              {tr(t.label)}{t.badge || null}
            </Pill>
          </Fragment>
        ))}
        {!shown.length && <span className="px-2 text-xs text-slate-500" data-testid={`${testIdPrefix}-no-match`}>No section matches</span>}
      </PillRail>
      {actions ? <div className="ml-auto shrink-0">{actions}</div> : null}
    </div>
  );
}

const CALLOUT_TONES = {
  info: { box: "bg-slate-50 border-slate-200 text-slate-600", icon: Info, iconClass: "text-slate-400" },
  warn: { box: "bg-amber-50 border-amber-200 text-amber-800", icon: AlertTriangle, iconClass: "text-amber-500" },
  danger: { box: "bg-rose-50 border-rose-200 text-rose-800", icon: ShieldAlert, iconClass: "text-rose-500" },
  success: { box: "bg-emerald-50 border-emerald-200 text-emerald-800", icon: CheckCircle2, iconClass: "text-emerald-500" },
};

export const Callout = ({ tone = "info", children, testId, icon }) => {
  const t = CALLOUT_TONES[tone] || CALLOUT_TONES.info;
  const Icon = icon || t.icon;
  return (
    <div className={`flex items-start gap-2 px-3 py-2 rounded-lg border text-xs ${t.box}`} data-testid={testId}>
      <Icon className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${t.iconClass}`} />
      <div className="min-w-0">{children}</div>
    </div>
  );
};

export const EmptyState = ({ icon: Icon, title, hint, testId, children }) => (
  <div className="text-center py-12" data-testid={testId}>
    {Icon ? <Icon className="w-8 h-8 mx-auto mb-3 text-slate-300" /> : null}
    <div className="text-sm font-medium text-slate-500">{title}</div>
    {hint ? <p className="text-xs text-slate-400 mt-1">{hint}</p> : null}
    {children ? <div className="mt-4 flex justify-center">{children}</div> : null}
  </div>
);

export const LoadingState = ({ label = "Loading…", testId }) => (
  <div className="flex items-center justify-center gap-2 py-12 text-sm text-slate-400" data-testid={testId}>
    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-slate-400" />
    {label}
  </div>
);

// Shared table chrome so row height + header casing match everywhere.
export const TABLE_HEAD_CLASS = "bg-slate-50 border-b border-slate-200 text-[11px] uppercase tracking-wide text-slate-500";
export const TABLE_CELL_CLASS = "px-3 py-2";
